import { Grid, makeStyles, Theme, Typography, useTheme } from "@material-ui/core";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import { Stack } from "@mui/material";
import { FunctionComponent, useContext } from "react";
import { Trans, useTranslation } from "react-i18next";
import { BeatLoader } from "react-spinners";
import { GeneDataGroupType } from "../../../../api/smartmelts-api/graphql/generated";
import { NutritionIcon } from "../../../../assets/icons/nutrition-icon";
import { ContentText } from "../../../../components/text/content-text";
import { HeadingText } from "../../../../components/text/heading-text";
import { useUser } from "../../../../components/user/user-context";
import { MenuItemCategory, MenuItemSubcategory } from "../../../../models/types/category-types";
import { NutritionContext } from "../../context/nutrition-context";
import { useTolerances } from "../../hooks/use-tolerances";
import { OverviewTolerance } from "./components/overview-tolerance";

const useStyles = makeStyles((theme: Theme) => ({
  container: {},
  hero: {
    width: "100%",
  },
  emphasized: {
    color: theme.palette.primary.main,
    fontWeight: 700,
  },
  toleranceWrapper: {
    paddingBottom: theme.spacing(1),
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(2),
    },
  },
  arrow: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  arrowText: {
    fontWeight: 300,
    marginLeft: theme.spacing(2),
  },
  headingContainer: {
    marginBottom: theme.spacing(1),
  },
  contentContainer: {
    marginBottom: theme.spacing(2),
    whiteSpace: "pre-wrap",
  },
}));

interface IOverviewProps {}

export const Overview: FunctionComponent<IOverviewProps> = (props) => {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const { gluten, lactose, caffeine, loading } = useTolerances();
  const { isDemo, userInformation } = useUser();

  const { changeActiveCategories } = useContext(NutritionContext);

  return (
    <Grid container className={classes.container}>
      <Grid item container direction="row" className={classes.headingContainer}>
        {userInformation && (
          <HeadingText>
            {t(`overview.heading`, { firstname: userInformation?.firstname, lastname: userInformation?.lastname })}
          </HeadingText>
        )}
      </Grid>
      <Grid item container direction="row" className={classes.contentContainer}>
        <ContentText light>{t(`overview.subheading${isDemo ? "_demo" : ""}_1`)}</ContentText>
      </Grid>
      {isDemo && (
        <Grid item container direction="row" className={classes.contentContainer}>
          <ContentText light>{t(`overview.subheading${isDemo ? "_demo" : ""}_2`)}</ContentText>
        </Grid>
      )}
      <Grid item container direction="row" className={classes.contentContainer}>
        <ContentText light small>
          <Trans
            i18nKey={`overview.subheading${isDemo ? "_demo" : ""}_3`}
            components={[<span className={classes.emphasized} />]}
          />
        </ContentText>
      </Grid>
      <Grid item container direction="row" style={{ paddingTop: theme.spacing(4) }}>
        {!loading && (
          <>
            <Grid item xs={12} sm={4} md={12} lg={4} className={classes.toleranceWrapper}>
              <OverviewTolerance
                icon={gluten.icon}
                name={gluten.name}
                labelColor={gluten.labelColor}
                label={gluten.label}
                onClick={() => changeActiveCategories(MenuItemCategory.DIET, MenuItemSubcategory.GLUTEN)}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={12} lg={4} className={classes.toleranceWrapper}>
              <OverviewTolerance
                icon={lactose.icon}
                name={lactose.name}
                labelColor={lactose.labelColor}
                label={lactose.label}
                onClick={() => changeActiveCategories(MenuItemCategory.DIET, MenuItemSubcategory.LACTOSE)}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={12} lg={4} className={classes.toleranceWrapper}>
              <OverviewTolerance
                icon={caffeine.icon}
                name={caffeine.name}
                labelColor={caffeine.labelColor}
                label={caffeine.label}
                buttonDisabled={isDemo}
                onClick={() =>
                  changeActiveCategories(
                    MenuItemCategory.METABOLISM,
                    MenuItemSubcategory.DETOX,
                    4,
                    GeneDataGroupType.DETOX,
                  )
                }
              />
            </Grid>
          </>
        )}
        {loading && (
          <Grid container justify={"center"}>
            <BeatLoader size={12} color={theme.palette.primary.main} />
          </Grid>
        )}
      </Grid>

      <Grid item container direction="row" className={classes.arrow}>
        <Stack direction="row" alignItems="center">
          <ArrowForwardIcon color="primary" />
          <Typography className={classes.arrowText} color="primary">
            {t("overview.arrow_text")}
          </Typography>
          <NutritionIcon color={theme.palette.primary.main} height="24" />
        </Stack>
      </Grid>
    </Grid>
  );
};
